import { ResourceIsoLanguage } from '../models/resources/resources.model';

export class LanguageHelper {

  /**
   * Normalizes an IETF language tag for comparison ("de_CH" / "DE-ch" -> "de-ch").
   */
  private static normalize( code: string ): string {
    return code.trim().toLowerCase().replace(/_/g, '-');
  }

  /**
   * Resolves any valid IETF language tag to a code that is actually offered by the
   * /api/Resources/IsoLanguages call. Falls back to the region neutral variant
   * ("de-CH" -> "de") and, if that is not supported either, to the first regional
   * variant of the same language ("de" -> "de-DE"). Returns an empty string when
   * the language is not supported at all.
   */
  static resolveIsoCode( code: string, languages: ResourceIsoLanguage[] ): string {
    if ( !code || !languages?.length ) {
      return '';
    }

    const target = LanguageHelper.normalize(code);
    const exact = languages.find(language => LanguageHelper.normalize(language.Code) === target);

    if ( exact ) {
      return exact.Code;
    }

    const base = target.split('-')[0];
    const neutral = languages.find(language => LanguageHelper.normalize(language.Code) === base);

    if ( neutral ) {
      return neutral.Code;
    }

    return languages.find(language => LanguageHelper.normalize(language.Code).split('-')[0] === base)?.Code ?? '';
  }

  /**
   * Languages Finstral does business in. They are listed on top of the select,
   * alphabetically among themselves, before all remaining languages.
   */
  private static readonly PRIORITY_CODES = [
    'de',    // German
    'it',    // Italian
    'fr',    // French
    'es',    // Spanish
    'en',    // English
    'nl',    // Dutch
    'nl-be', // Flemish
    'pt'     // Portuguese
  ];

  /**
   * Drops inactive languages and sorts the rest alphabetically for the select,
   * with the Finstral languages grouped on top.
   */
  static prepareOptions( languages: ResourceIsoLanguage[] ): ResourceIsoLanguage[] {
    const isPriority = ( language: ResourceIsoLanguage ) =>
      LanguageHelper.PRIORITY_CODES.includes(LanguageHelper.normalize(language.Code ?? ''));

    return (languages ?? [])
      .filter(language => !language.Inactive)
      .sort(( a, b ) => {
        const priorityDiff = Number(isPriority(b)) - Number(isPriority(a));

        return priorityDiff !== 0 ? priorityDiff : (a.Name ?? '').localeCompare(b.Name ?? '');
      });
  }
}
